import { Fragment } from 'react';
import { Box, Typography } from '@mui/material';
import { SCHOOLS } from './tokens';

const PLOT_HEIGHT = 120;

export type SchoolColumn = {
  label: string;
  value: number;
  color?: string;
  emphasised?: boolean;
};

export interface SchoolColumnChartProps {
  title: string;
  columns: SchoolColumn[];
  /** Draws a dashed rule immediately after this column index. */
  thresholdAfterIndex?: number;
}

function roundedMax(values: number[]): number {
  const max = Math.max(0, ...values);
  if (max === 0) return 1;
  return Math.ceil(max / 10) * 10;
}

export function SchoolColumnChart({
  title,
  columns,
  thresholdAfterIndex,
}: SchoolColumnChartProps) {
  const scale = roundedMax(columns.map((column) => column.value));

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        p: '16px',
        borderRadius: '8px',
        bgcolor: SCHOOLS.surface,
      }}
    >
      <Typography
        component="div"
        sx={{
          fontFamily: SCHOOLS.font,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: '24px',
          color: SCHOOLS.muted,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
        {columns.map((column, index) => (
          <Fragment key={column.label}>
            <Box
              data-testid="school-column"
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box
                component="span"
                data-testid="school-column-value"
                sx={{
                  fontFamily: SCHOOLS.font,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: '16px',
                  color: column.emphasised === true ? SCHOOLS.emphasis : SCHOOLS.ink,
                }}
              >
                {column.value}
              </Box>
              <Box
                data-testid="school-column-bar"
                sx={{
                  width: '100%',
                  height: `${Math.round((column.value / scale) * PLOT_HEIGHT)}px`,
                  borderRadius: '3px 3px 0 0',
                  bgcolor: column.color ?? SCHOOLS.ghost,
                }}
              />
            </Box>
            {thresholdAfterIndex === index && (
              <Box
                data-testid="school-column-threshold"
                sx={{
                  width: '1px',
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  borderLeft: `1px dashed ${SCHOOLS.thresholdRule}`,
                }}
              />
            )}
          </Fragment>
        ))}
      </Box>

      <Box sx={{ height: '1px', bgcolor: SCHOOLS.border }} />

      <Box sx={{ display: 'flex', gap: '10px' }}>
        {columns.map((column, index) => (
          <Fragment key={column.label}>
            <Box
              component="span"
              data-testid="school-column-label"
              sx={{
                flex: 1,
                minWidth: 0,
                textAlign: 'center',
                fontFamily: SCHOOLS.font,
                fontSize: 12,
                fontWeight: column.emphasised === true ? 700 : 600,
                lineHeight: '16px',
                color: column.emphasised === true ? SCHOOLS.emphasis : SCHOOLS.muted,
              }}
            >
              {column.label}
            </Box>
            {thresholdAfterIndex === index && <Box sx={{ width: '1px', flexShrink: 0 }} />}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}
